import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { LanguagePipe } from '../../../../../pipe/language.pipe';
import { PartitionProperty } from '../../../../../util/project-partition';

export interface DraftPartitionPromptData {
  /** Name of the Project the item is currently in; may be blank. */
  projectName: string;
  /** Which partition key the user just changed. */
  property: PartitionProperty;
  /** Raw value the item now has, e.g. `'COLOR'`. */
  newValue: string;
  /** Raw value every other active member shares, e.g. `'GRAY'`. */
  projectValue: string;
}

/**
 * `KEEP` and `MOVE` are both deliberate answers. Dismissing the dialog
 * (Esc / backdrop) closes with `undefined`, which is NOT the same thing —
 * see the class comment.
 */
export type DraftPartitionPromptResult = 'KEEP' | 'MOVE';

/**
 * "This Draft is now COLOR, but this Project is GRAY. Keep it here, or move
 * it to a new Project?"
 *
 * Raised when a user changes `bindingType` / `interiorType` on a Draft and
 * that edit splits a previously-uniform Project. The decision of *whether*
 * to raise it lives in `util/project-partition.ts`, which is where the
 * suppression rules and their reasoning are; this component only asks.
 *
 * ## Three things the server was explicit about (2026-10-17c §2)
 *
 * 1. **"Keep it here" is a legitimate answer and must be effortless.** Mixed
 *    Projects are legal and no server code rejects one. This dialog is not
 *    trying to herd anyone toward splitting — only to make sure a split does
 *    not silently fail to happen. So Keep is a single click, it is the
 *    first button, and Esc does not do anything worse than it.
 * 2. **Either answer is an edit.** The user has now inspected the Project,
 *    so the caller freezes it from the reconciler. That is why the two
 *    answers are distinct values rather than a boolean.
 * 3. **Dismissal is not an answer.** Esc / backdrop returns `undefined`, and
 *    the caller treats that as "no decision": nothing moves AND nothing is
 *    marked as edited. Recording a dismissal as a deliberate "keep" would
 *    freeze a Project from the reconciler on the strength of a keystroke
 *    that means "go away", which is exactly the kind of invisible state
 *    change that makes people distrust an interface.
 */
@Component({
  selector: 'app-draft-partition-prompt',
  templateUrl: './draft-partition-prompt.component.html',
  styleUrls: ['./draft-partition-prompt.component.css']
})
export class DraftPartitionPromptComponent {

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DraftPartitionPromptData,
    private dialogRef: MatDialogRef<DraftPartitionPromptComponent, DraftPartitionPromptResult>
  ) {}

  /** "Interior" or "Binding" — the subtitle, so the user knows what changed. */
  get propertyLabel(): string {
    const id = this.data && this.data.property === 'bindingType' ? 'Binding' : 'Interior';
    return LanguagePipe.get(id, 'compressSpaces');
  }

  get newValueLabel(): string {
    return this.valueLabel(this.data ? this.data.newValue : '');
  }

  get projectValueLabel(): string {
    return this.valueLabel(this.data ? this.data.projectValue : '');
  }

  /**
   * The Project's own name, trimmed; blank when it has none.
   *
   * The "This Project" fallback is rendered by the TEMPLATE rather than
   * substituted here. A string only reaches a translation bundle if the
   * extractor can see it, and the extractor reads templates - a literal
   * passed to `LanguagePipe.get()` from TypeScript is invisible to it and
   * would have shipped as untranslated English in all 21 locales.
   */
  get projectName(): string {
    return this.data && this.data.projectName ? ('' + this.data.projectName).trim() : '';
  }

  /**
   * `GRAY` / `COLOR` / `PERFECT` / `COIL` are themselves translation ids and
   * already carry proper English in every bundle.
   *
   * A value with no row — `PUR` and the other binding types the server
   * confirmed are real but that this client cannot yet render — falls back to
   * the raw token rather than rendering blank. Showing `PUR` is ugly; showing
   * nothing would make the dialog claim the Project has no binding at all.
   */
  private valueLabel(value: string): string {
    if (!value) {
      return '';
    }
    return LanguagePipe.get(value, 'compressSpaces');
  }

  keep(): void {
    this.dialogRef.close('KEEP');
  }

  move(): void {
    this.dialogRef.close('MOVE');
  }
}
